import { SimpleGrid, Stack, Text } from "@chakra-ui/react";
import React from "react";
import { useQuery } from "react-query";
import { trackEvent } from "../../../shared/analytics";
import Spinner from "../../../shared/components/Spinner";
import BrowseSectionTitle from "../../browse/components/BrowseSectionTitle";
import { getScreenReleases } from "../../../services/screenReleasesApi";
import {
  getReleaseSections,
  releasesQueryKey,
  releasesQueryOptions,
} from "../helpers/releases";
import ReleaseCard from "./ReleaseCard";

const LIMIT = 3;

/**
 * "Upcoming" and "Recently released" rows for one cinematic universe,
 * shown above that universe's reading orders.
 * Mirrors the mobile ReleaseSections component.
 */
const ReleaseSections = ({ releaseFranchise }) => {
  const query = useQuery(
    releasesQueryKey(releaseFranchise.slug),
    () =>
      getScreenReleases(releaseFranchise.slug, {
        upcomingLimit: LIMIT,
        releasedLimit: LIMIT,
      }),
    releasesQueryOptions
  );
  const sections = React.useMemo(
    () => getReleaseSections(query.data),
    [query.data]
  );

  if (query.isError) {
    // The reading orders below still work; just skip the release rows.
    return null;
  }

  if (query.isLoading) {
    return <Spinner />;
  }

  const renderSection = (title, key, items, emptyText) => (
    <section aria-label={title}>
      <BrowseSectionTitle mt="2">{title}</BrowseSectionTitle>
      {items.length ? (
        <Stack spacing="12px">
          {items.map((release) => (
            <ReleaseCard
              key={`${release.id}-${release.type}-${release.seasonNumber}`}
              release={release}
              onClick={() =>
                trackEvent("browse_release_click", {
                  tab: releaseFranchise.key,
                  section: key,
                  releaseId: release.id,
                  hasReadingOrder: release.readingOrders?.length > 0,
                })
              }
            />
          ))}
        </Stack>
      ) : (
        <Text fontSize="sm" color="gray.400">
          {emptyText}
        </Text>
      )}
    </section>
  );

  // Upcoming on the left, recently released on the right (stacked on phones).
  return (
    <SimpleGrid columns={{ base: 1, md: 2 }} spacingX="24px" spacingY="0">
      {renderSection(
        "UPCOMING",
        "upcoming",
        sections.upcoming,
        "Nothing announced yet."
      )}
      {renderSection(
        "RECENTLY RELEASED",
        "released",
        sections.released,
        "Nothing released recently."
      )}
    </SimpleGrid>
  );
};

export default ReleaseSections;
